import { CLASS_IDS, DIFFICULTIES, MODES, PING_KINDS, type PingKind } from "@eldrun/content";
import { z } from "zod";

/**
 * Mensagens cliente → servidor. Os mesmos esquemas validam no servidor (via `validate`, que aceita
 * Standard Schema) e tipam o envio no cliente.
 */
export const JoinOptions = z.object({
  name: z.string().max(64).optional(),
  mode: z.enum(MODES).default("mutirao"),
  difficulty: z.enum(DIFFICULTIES).default("arretado"),
  /** Sala privada (entra só quem tem o código). */
  private: z.boolean().default(false),
  /** Revanche: a sala nova já nasce com esta seed (código de 7 caracteres). */
  seed: z.string().max(16).optional(),
  /** Sessão da conta (JWT da API; F32): o servidor valida ao entrar; sem ele, convidado. */
  token: z.string().max(4096).optional(),
  /** "Jogar sozinho" (F54): sala privada da Irmandade, só para quem cria, que começa na hora. */
  solo: z.boolean().default(false),
  /** Classe escolhida no menu (F64); sem ela, a sugerida pelo slot. Muda no lobby. */
  classId: z.enum(CLASS_IDS).optional(),
});
export type JoinOptions = z.input<typeof JoinOptions>;

export const ReadyMessage = z.object({ ready: z.boolean() });
export type ReadyMessage = z.infer<typeof ReadyMessage>;

/** Habilidade (F65): a ativa ou a ultimate da própria classe, com o ponto mirado (o servidor limita o alcance). */
export const SkillMessage = z.object({ slot: z.enum(["ativa", "ultimate"]), x: z.number().finite(), z: z.number().finite() });
export type SkillMessage = z.infer<typeof SkillMessage>;

/** Juramento da Alvorada escolhido (F70): um dos oferecidos. */
export const OathMessage = z.object({ id: z.string().min(1).max(40) });
export type OathMessage = z.infer<typeof OathMessage>;

/** Oferta do Juramento da Alvorada (servidor → o jogador, F70): as opções (vazia limpa). */
export interface OathOffer {
  options: string[];
}

/** Classe no lobby (F64): repetidas no grupo valem (dividem a cor da classe, F85). */
export const ChooseClassMessage = z.object({ classId: z.enum(CLASS_IDS) });
export type ChooseClassMessage = z.infer<typeof ChooseClassMessage>;

export const ConfigureMessage = z.object({
  mode: z.enum(MODES).optional(),
  difficulty: z.enum(DIFFICULTIES).optional(),
  /** Texto da seed (vazio = sorteada). Só em sala privada. */
  seed: z.string().max(32).optional(),
  /** Fogo amigo no Mutirão (na Treta é sempre ligado). */
  friendlyFire: z.boolean().optional(),
});
export type ConfigureMessage = z.infer<typeof ConfigureMessage>;

export const StartMessage = z.object({}).optional();
export const KickMessage = z.object({ sessionId: z.string().min(1).max(64) });
export type KickMessage = z.infer<typeof KickMessage>;

export const LoadedMessage = z.object({
  worldHash: z.number().int().min(0).max(0xffffffff),
  resourceHash: z.number().int().min(0).max(0xffffffff),
});
export type LoadedMessage = z.infer<typeof LoadedMessage>;

/**
 * Construir uma peça da grade (doc 03): o item do slot na célula (gx, gz), nível gy e giro (×90°).
 * O servidor confere item, alcance, ocupação, colisões, suporte e o limite de peças.
 */
export const BuildMessage = z.object({
  slot: z.number().int().min(0).max(23),
  gx: z.number().int().min(-600).max(600),
  gy: z.number().int().min(-50).max(200),
  gz: z.number().int().min(-600).max(600),
  rot: z.number().int().min(0).max(3),
});
export type BuildMessage = z.infer<typeof BuildMessage>;
/** Desmontar uma peça (só quem construiu, ou peças de quem saiu da sala; doc 07, I3). */
export const DismantleMessage = z.object({ id: z.string().min(1).max(20) });
/** Abrir ou fechar uma porta. */
export const DoorMessage = z.object({ id: z.string().min(1).max(20) });

/** Abrir uma estação de processo (o servidor confere a distância de 5 m). */
export const StationOpenMessage = z.object({ id: z.string().min(1).max(20) });
export type StationOpenMessage = z.infer<typeof StationOpenMessage>;
/** Fechar a estação aberta. */
export const StationCloseMessage = z.object({});

/** A estação aberta fechou sozinha (servidor → cliente): longe demais ou ela sumiu. */
export interface StationClosedEvent {
  id: string;
}

/** Abrir (comprar) um Baú de Simpatia a até 3 m (o servidor confere saldo e preço do dia). */
export const SimpatiaOpenMessage = z.object({ id: z.string().min(1).max(20) });
export type SimpatiaOpenMessage = z.infer<typeof SimpatiaOpenMessage>;

/** Simpatia ganha (servidor → todos): quem, qual e de que baú. */
export interface SimpatiaGainedEvent {
  sessionId: string;
  simpatia: string;
  chest: string;
}

/** Negócio com o mascate: comprar (o que ele vende) ou vender (o que ele compra), pela posição da oferta. */
export const TradeMessage = z.object({
  id: z.string().min(1).max(20),
  side: z.enum(["comprar", "vender"]),
  index: z.number().int().min(0).max(15),
});
export type TradeMessage = z.infer<typeof TradeMessage>;

/** Negócio feito (servidor → quem negociou). */
export interface TradeDoneEvent {
  side: "comprar" | "vender";
  item: string;
  qty: number;
  price: number;
}

/** Segurar (ou soltar) a interação de reviver na Cruz de um aliado (doc 02 §16). */
export const ReviveMessage = z.object({ id: z.string().min(1).max(20), holding: z.boolean() });
export type ReviveMessage = z.infer<typeof ReviveMessage>;

/** Pagar na Carranca para reviver um aliado morto (doc 02 §17). */
export const CarrancaMessage = z.object({ id: z.string().min(1).max(20), target: z.string().min(1).max(64) });
export type CarrancaMessage = z.infer<typeof CarrancaMessage>;

/** Irmandade sozinho, morto (F54): pagar a Estátua da Fênix com a própria bolsa ou desistir (derrota). */
export const SoloDeathMessage = z.object({ choice: z.enum(["pagar", "desistir"]) });
export type SoloDeathMessage = z.infer<typeof SoloDeathMessage>;

/** Só nos e2e (servidor com `ELDRUN_TEST_HOOKS=1`): dano no próprio corpo. */
export const TestDamageMessage = z.object({ amount: z.number().min(0).max(10_000) });

/** Tocar numa Pedra de Encanto (doc 01): Invocação de chefe ou Desafio, conforme a Pedra. */
export const EncantoMessage = z.object({ id: z.string().min(1).max(20) });
export type EncantoMessage = z.infer<typeof EncantoMessage>;

/** Só nos e2e (servidor com `ELDRUN_TEST_HOOKS=1`): um chefe na frente do jogador, com a vida dada. */
export const TestBossMessage = z.object({ type: z.string().min(1).max(32), hp: z.number().int().min(1).max(60_000) });

/** Um chefe vai surgir (servidor → todos): rugido e marcador `inMs` antes, no ponto. */
export interface BossWarningEvent {
  type: string;
  x: number;
  z: number;
  inMs: number;
  source: "noite" | "pedra" | "final";
}

/** Fim de um chefe (servidor → todos): derrubado (por quem deu o golpe final) ou recuou. */
export interface BossEndEvent {
  id: string;
  type: string;
  result: "derrotado" | "recuou";
  by: string;
}

/** Alguém tocou uma Pedra de Encanto (servidor → todos): o encanto começa em `inMs`. */
export interface EncantoEvent {
  id: string;
  kind: "chefe" | "desafio" | "guardiao";
  by: string;
  inMs: number;
}

/** Segurar (ou soltar) a Peneira num redemoinho do Saci (doc 06). */
export const PeneiraMessage = z.object({ id: z.string().min(1).max(20), holding: z.boolean() });
export type PeneiraMessage = z.infer<typeof PeneiraMessage>;

/** Só nos e2e (servidor com `ELDRUN_TEST_HOOKS=1`): força a Mandinga, põe um redemoinho a `whirlwind` m ou aplica uma travessura. */
export const TestSaciMessage = z.object({ mandinga: z.string().max(32).optional(), whirlwind: z.number().min(1).max(200).optional(), prank: z.string().max(20).optional() });

/** Travessura do Saci (servidor → todos): quem, qual e, na troca, com quem (que acontece em `inMs`). */
export interface TravessuraEvent {
  sessionId: string;
  kind: string;
  other: string;
  inMs: number;
}

/** O Saci foi capturado com a Peneira (servidor → todos): os redemoinhos do dia somem. */
export interface SaciCalmEvent {
  by: string;
}

/** Uma Pedra Fulminada vai cair (servidor → todos): o ponto e quanto falta. */
export interface MeteorEvent {
  x: number;
  z: number;
  inMs: number;
}

/** Depositar na etapa atual de um componente da Jangada o que tiver no inventário (doc 01). */
export const RaftDepositMessage = z.object({ part: z.enum(["casco", "mastro", "vela", "leme", "altar"]) });
export type RaftDepositMessage = z.infer<typeof RaftDepositMessage>;

/** Zarpar (Leme): começa a contagem com todos no convés. */
export const RaftSailMessage = z.object({});

/** Revanche (tela de fim): uma sala nova com o mesmo grupo, repetindo ou não a seed. */
export const RematchMessage = z.object({ repeatSeed: z.boolean() });

/** Só nos e2e (servidor com `ELDRUN_TEST_HOOKS=1`): conserta a Jangada e enche o Relicário; ou dá a vida do Boitatá. */
export const TestRaftMessage = z.object({ complete: z.boolean().optional(), boitataHp: z.number().int().min(1).max(60_000).optional(), killBoitata: z.boolean().optional() });

/** Só nos e2e (servidor com `ELDRUN_TEST_HOOKS=1`): o Cerco já fechado neste raio em volta de quem pediu. */
export const TestCercoMessage = z.object({ radius: z.number().min(0).max(2000) });

/**
 * Só nos e2e e no teste de carga (servidor com `ELDRUN_TEST_HOOKS=1`, F36): `n` assombrações em volta
 * de quem pediu (até 40, o teto do diretor da noite), sem dano (os orçamentos medem o pior caso de
 * criaturas na tela sem matar ninguém).
 */
export const TestCreaturesMessage = z.object({ n: z.number().int().min(1).max(40) });

/** Só nos e2e (servidor com `ELDRUN_TEST_HOOKS=1`): leva quem pediu ao chão livre mais perto de (x, z). */
export const TestGotoMessage = z.object({ x: z.number().min(-2000).max(2000), z: z.number().min(-2000).max(2000) });

/** Sala da revanche pronta (servidor → todos): o código para entrar. */
export interface RematchEvent {
  code: string;
}

/** Áreas que machucam apagadas antes da hora (transição de fase do Boitatá limpa o convés). */
export interface HazardClearEvent {
  ids: string[];
}

/** Arremesso de criatura parado pela cúpula do Bastião (F96): onde bateu e de quem é a cúpula. */
export interface LobBlockedEvent {
  /** A criatura que arremessou. */
  id: string;
  /** Dono da cúpula. */
  by: string;
  x: number;
  y: number;
  z: number;
}

/** Jangada achada (servidor → todos): quem achou ("" pelo mapa). */
export interface RaftFoundEvent {
  by: string;
}

/** Muiraquitã conquistado (servidor → todos): vai direto ao Relicário do grupo. */
export interface MuiraquitaEvent {
  color: string;
  by: string;
}

/** Fim de um Desafio (servidor → todos). */
export interface ChallengeEndEvent {
  stone: string;
  result: "vencido" | "falhou";
  by: string;
}

/** Alguém voltou (servidor → todos): quem, quem reviveu e onde. */
export interface RevivedEvent {
  sessionId: string;
  by: string;
  at: "cruz" | "carranca" | "jangada" | "fenix";
}

/** Achado lendário num baú (servidor → todos): anunciado no HUD (doc 03). */
export interface LootFoundEvent {
  sessionId: string;
  itemId: string;
  /** `itemId` da estrutura do baú (o nome vem de `structureName`). */
  chest: string;
}

/** Golpe de coleta num recurso (o servidor valida alcance, ferramenta e cadência). */
export const HarvestMessage = z.object({ resourceId: z.number().int().min(0).max(0xffffffff) });
export type HarvestMessage = z.infer<typeof HarvestMessage>;

/** Endereço de um slot: inventário (0–23), armadura (0–3), Aljava (0) ou a estação aberta (0–5). */
export const SlotRefSchema = z.object({
  area: z.enum(["inv", "armor", "quiver", "estacao"]),
  index: z.number().int().min(0).max(23),
});
const qty = z.number().int().min(1).max(9999).optional();
export const InventoryMoveMessage = z.object({ from: SlotRefSchema, to: SlotRefSchema, qty });
export const InventorySlotMessage = z.object({ slot: SlotRefSchema });
export const InventoryDiscardMessage = z.object({ slot: SlotRefSchema, qty });
export const InventoryGatherMessage = z.object({ itemId: z.string().min(1).max(40) });
export const InventorySelectMessage = z.object({ index: z.number().int().min(0).max(7) });
export type InventoryMoveMessage = z.infer<typeof InventoryMoveMessage>;
export type InventoryDiscardMessage = z.infer<typeof InventoryDiscardMessage>;

export const CraftMessage = z.object({ recipeId: z.string().min(1).max(40), times: z.number().int().min(1).max(10) });
export type CraftMessage = z.infer<typeof CraftMessage>;

/** Posicionar o item de um slot do inventário no mundo (estações; a F17 traz a construção). */
export const PlaceMessage = z.object({
  slot: z.number().int().min(0).max(23),
  x: z.number().min(-1100).max(1100),
  z: z.number().min(-1100).max(1100),
  yaw: z.number().min(-10).max(10),
});
export type PlaceMessage = z.infer<typeof PlaceMessage>;

/**
 * Golpe corpo a corpo com o item na mão. `rtt` é a estimativa do cliente (ms), usada só para
 * rebobinar os alvos até o que ele via, com teto por modo (doc 07, A7).
 */
export const AttackMessage = z.object({});
export type AttackMessage = z.infer<typeof AttackMessage>;

/**
 * Puxar a corda (cliente → servidor; doc 02 §12). `{}` começa a puxar: o servidor mede a carga do
 * início ao soltar no próprio relógio (as duas mensagens viajam com a mesma latência). `{ stop: true }`
 * desiste sem atirar (painel aberto, morreu, acabou a flecha, item bloqueado): só encerra a puxada
 * que os outros veem, sem mexer na carga medida.
 */
export const DrawMessage = z.object({ stop: z.literal(true).optional() });
export type DrawMessage = z.infer<typeof DrawMessage>;

/** Alguém começou ou parou de puxar a corda (servidor → todos menos quem puxa): só para animar. */
export interface BowDrawEvent {
  sessionId: string;
  /** true = começou a puxar a corda; false = soltou, atirou ou desistiu. */
  pulling: boolean;
}

/**
 * Soltar a flecha (cliente → servidor): um contador do próprio cliente, que volta nos eventos para
 * casar a flecha prevista com a do servidor (ou com a recusa).
 */
export const ShootMessage = z.object({ shot: z.number().int().min(0).max(1_000_000) });
export type ShootMessage = z.infer<typeof ShootMessage>;

/** Disparo recusado (servidor → atirador): sem arco, sem flecha ou antes do rearme. */
export interface ShotRefusedEvent {
  shot: number;
}

/**
 * Medida de latência (doc 07, A7): o servidor manda `ping { n }` e guarda quando mandou; o cliente
 * devolve `pong { n }`. A rebobinagem usa o RTT medido assim, não um valor informado pelo cliente.
 */
export const PongMessage = z.object({ n: z.number().int().min(0).max(4_294_967_295) });
export type PongMessage = z.infer<typeof PongMessage>;
export interface PingEvent {
  n: number;
  /** RTT suavizado que o servidor mediu deste cliente, em ms (o `/latencia` mostra). */
  rtt: number;
}

/**
 * Chat (doc 06). O esquema aceita até 512 caracteres para não derrubar quem colar um texto longo; o
 * servidor limpa, corta em 140 e filtra antes de repassar.
 */
export const ChatMessage = z.object({ text: z.string().max(512) });
export type ChatMessage = z.infer<typeof ChatMessage>;
/** Fala repassada (servidor → quem pode ver): `dead` marca o canal dos caídos na Treta. */
export interface ChatEvent {
  from: string;
  name: string;
  text: string;
  dead: boolean;
}

/** Comandos que o servidor decide; os demais (`/semente`, `/latencia`…) respondem no cliente. */
export const SERVER_COMMANDS = ["expulsar", "votar", "destravar"] as const;
export const CommandMessage = z.object({ name: z.enum(SERVER_COMMANDS), arg: z.string().max(64) });
export type CommandMessage = z.infer<typeof CommandMessage>;

/** Ping (cliente → servidor): ponto e tipo pela mira; `target` é a criatura mirada, se houver. */
export const MarkMessage = z.object({
  x: z.number().min(-1100).max(1100),
  y: z.number().min(-200).max(600),
  z: z.number().min(-1100).max(1100),
  kind: z.enum(PING_KINDS),
  target: z.string().max(32).optional(),
});
export type MarkMessage = z.infer<typeof MarkMessage>;
/** Ping aceito (servidor → grupo; na Treta, só a quem marcou). */
export interface MarkEvent {
  id: number;
  by: string;
  x: number;
  y: number;
  z: number;
  kind: PingKind;
  /** Duração em ms. */
  ms: number;
}

/**
 * Mensagens de sistema do chat (doc 06): chave de tradução + parâmetros (nomes, já sanitizados),
 * nunca texto livre. O cliente monta a frase.
 */
export const SYSTEM_KEYS = [
  "saiu",
  "caiu-conexao",
  "voltou",
  "morreu",
  "reviveu",
  "invocou",
  "drop-raro",
  "votacao-aberta",
  "votacao-falhou",
  "expulso-votacao",
  "expulso-equipe",
  "destravou",
  "interrompeu",
  "caiu",
  "levantou",
] as const;
export type SystemKey = (typeof SYSTEM_KEYS)[number];
export interface SystemEvent {
  key: SystemKey;
  params: string[];
}

/** Flecha disparada (servidor → todos): origem e velocidade; cada cliente simula a mesma curva. */
export interface ArrowEvent {
  id: string;
  owner: string;
  /** Contador do disparo no cliente do dono e a flecha dentro do disparo (leque). */
  shot: number;
  index: number;
  itemId: string;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
}

/** Orbe do cajado (F98, servidor → todos): voa reto da origem com a velocidade, até `range` m. */
export interface OrbEvent {
  id: string;
  owner: string;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  range: number;
}

/** Fim do orbe (servidor → todos): onde parou e se pegou num corpo. */
export interface OrbEndEvent {
  id: string;
  owner: string;
  x: number;
  y: number;
  z: number;
  hit: boolean;
}

/** Fim da flecha (servidor → todos): onde parou e se ficou cravada (no chão, num tronco). */
export interface ArrowEndEvent {
  id: string;
  owner: string;
  shot: number;
  index: number;
  x: number;
  y: number;
  z: number;
  stuck: boolean;
}

/** Alguém golpeou (servidor → todos): só para animar o golpe dos outros. */
export interface SwingEvent {
  sessionId: string;
  itemId: string;
  /** Duração do ciclo em segundos, quando difere do catálogo (Café Coado). */
  cycle?: number;
}

/** De onde veio um dano (Fase 12, Gate C): só para o desenho, nenhuma regra lê. */
export type DamageKind = "golpe" | "orbe" | "flecha" | "habilidade" | "eco" | "criatura";

/**
 * Passivas de classe que agiram num dano (Fase 12, Gate C), só para o desenho: o Ímpeto gasto no
 * golpe, o Olho de Falcão (o bônus de longe ou a marca da carga cheia) e o que o Escudo Erguido segurou.
 * A Ressonância é o próprio `kind: "eco"`.
 */
export interface DamageProcs {
  impeto?: true;
  olho?: true;
  bloqueio?: number;
}

/** Dano confirmado pelo servidor (servidor → todos): números, marcador, tremor, vinheta. */
export interface DamageEvent {
  /** sessionId do alvo (criaturas na F13). */
  target: string;
  /** Quem bateu ("" = ambiente). */
  attacker: string;
  x: number;
  y: number;
  z: number;
  /** Dano que saiu de Patuá + Vida, e quanto o Patuá absorveu. */
  amount: number;
  absorbed: number;
  crit: boolean;
  weak: boolean;
  /** A proteção contra golpe fatal segurou o alvo. */
  saved: boolean;
  died: boolean;
  /** De onde veio (opcional, só visual; sem ele, o desenho de sempre). */
  kind?: DamageKind;
  /** Passivas que agiram (opcional, só visual). */
  procs?: DamageProcs;
}

/**
 * Uma criatura começou um ataque (servidor → todos): o cliente desenha o aviso no chão e sincroniza
 * a animação para acertar em `inMs` (tempo restante quando o servidor enviou; doc 06, Telegraphs).
 */
export interface CreatureAttackEvent {
  id: string;
  attack: string;
  /** Arco à frente, círculo ou faixa (`linha`, de `x, z` na direção do yaw). */
  shape: "arco" | "circulo" | "linha";
  /** Origem da forma: a criatura (golpe, salto, carga) ou o ponto de queda (mergulho, arremesso). */
  x: number;
  z: number;
  yaw: number;
  range: number;
  /** Abertura total do arco, em graus. */
  angle: number;
  /** Largura da faixa (0 nas outras formas). */
  width: number;
  inMs: number;
  /** Marcas de um mesmo ataque (chuva de caldeirões, raízes): cada uma com seu índice. */
  mark?: number;
  /** Anel: raio interno sem dano (chicote do Boitatá). */
  inner?: number;
  /** A área segue perigosa por mais isto depois do aviso (golpes que batem várias vezes). */
  activeMs?: number;
  /** Altura do chão do aviso, quando não é o piso do ponto: o alvo em cima da laje da Caverna. */
  y?: number;
}

/**
 * Área que machuca por um tempo (servidor → todos; doc 06, Bestiário): rastro de fogo da Mula
 * (segmento de `x, z` a `x2, z2` com meia-largura `radius`) ou poça do caldeirão da Cuca (círculo).
 */
export interface HazardEvent {
  id: string;
  kind: "fogo" | "poca" | "mato" | "brasa";
  x: number;
  z: number;
  x2: number;
  z2: number;
  radius: number;
  ms: number;
  /** Altura do chão da área, quando não é o piso do ponto: a poça em cima da laje da Caverna. */
  y?: number;
}

/** Receitas recém-descobertas (servidor → quem passou a vê-las). */
export interface UnlockedEvent {
  recipes: string[];
}

/** Itens que entraram no inventário (servidor → dono). */
export interface CollectedEvent {
  itemId: string;
  qty: number;
}

/** Golpe de coleta aceito (servidor → quem golpeou): o recurso e se ele caiu agora. */
export interface HarvestedEvent {
  id: number;
  removed: boolean;
}

/** Golpe de coleta aceito (servidor → todos menos quem colheu): só para animar o golpe dos outros. */
export interface HarvestSwingEvent {
  sessionId: string;
  itemId: string;
  /** Duração do ciclo da ferramenta, em segundos (a cadência real do golpe). */
  cycle: number;
}

/** Nomes das mensagens (uma fonte só para cliente e servidor). */
export const MSG = {
  ready: "pronto",
  skill: "habilidade",
  /** Servidor → todos: uma habilidade foi usada (os efeitos visuais). */
  skillUsed: "habilidade-usada",
  /** Servidor → quem usou: aceita, com a recarga (o cliente conta) ou recusada. */
  skillAck: "habilidade-ok",
  /** Juramento da Alvorada (F70): a oferta (servidor → jogador) e a escolha (jogador → servidor). */
  oathOffer: "juramento-oferta",
  oath: "juramento",
  /** Servidor → todos: um Elo fechou (F67), com quem participou. */
  elo: "elo",
  chooseClass: "classe",
  configure: "configurar",
  start: "iniciar",
  kick: "expulsar",
  loaded: "carregado",
  harvest: "golpear",
  invMove: "inv-mover",
  invSplit: "inv-dividir",
  invQuick: "inv-rapido",
  invGather: "inv-juntar",
  invDiscard: "inv-descartar",
  invSelect: "inv-selecionar",
  craft: "fabricar",
  place: "posicionar",
  attack: "atacar",
  /** cliente → servidor */
  shoot: "atirar",
  /** cliente → servidor */
  stationOpen: "estacao-abrir",
  /** cliente → servidor */
  build: "construir",
  /** cliente → servidor */
  dismantle: "desmontar",
  /** cliente → servidor */
  door: "porta",
  /** cliente → servidor */
  stationClose: "estacao-fechar",
  /** servidor → cliente */
  stationClosed: "estacao-fechada",
  /** servidor → todos */
  lootFound: "achado",
  /** cliente → servidor */
  simpatiaOpen: "simpatia-abrir",
  /** servidor → todos */
  simpatiaGained: "simpatia-ganha",
  /** cliente → servidor */
  trade: "negociar",
  /** servidor → cliente */
  tradeDone: "negocio",
  /** cliente → servidor */
  revive: "reviver",
  /** cliente → servidor */
  carranca: "carranca",
  /** cliente → servidor (F54) */
  soloDeath: "morte-solo",
  /** servidor → todos */
  revived: "revivido",
  /** cliente → servidor (só nos e2e) */
  testDamage: "teste-dano",
  /** cliente → servidor */
  encanto: "encanto",
  /** servidor → todos */
  encantoStarted: "encanto-inicio",
  /** servidor → todos */
  bossWarning: "chefe-aviso",
  /** servidor → todos */
  bossEnd: "chefe-fim",
  /** servidor → todos */
  challengeEnd: "desafio-fim",
  /** servidor → todos */
  muiraquita: "muiraquita",
  /** cliente → servidor */
  peneira: "peneira",
  /** servidor → todos */
  travessura: "travessura",
  /** servidor → todos */
  saciCalm: "saci-calmo",
  /** servidor → todos */
  meteor: "pedra-fulminada",
  /** cliente → servidor (só nos e2e) */
  testSaci: "teste-saci",
  /** cliente → servidor */
  raftDeposit: "jangada-depositar",
  /** cliente → servidor */
  raftSail: "zarpar",
  /** servidor → todos */
  raftFound: "jangada-achada",
  /** cliente → servidor */
  rematch: "revanche",
  /** servidor → todos */
  rematchReady: "revanche-pronta",
  /** servidor → todos */
  hazardClear: "perigo-fim",
  /** servidor → todos */
  lobBlocked: "arremesso-bloqueado",
  /** servidor → todos */
  orb: "orbe",
  /** servidor → todos */
  orbEnd: "orbe-fim",
  /** cliente → servidor (só nos e2e) */
  testRaft: "teste-jangada",
  /** cliente → servidor (só nos e2e) */
  testCerco: "teste-cerco",
  /** cliente → servidor (só nos e2e) */
  testBoss: "teste-chefe",
  /** cliente → servidor (só nos e2e e no teste de carga) */
  testCreatures: "teste-criaturas",
  /** cliente → servidor (só nos e2e) */
  testGoto: "teste-ir",
  /** cliente → servidor */
  draw: "puxar",
  /** servidor → todos menos quem puxa */
  bowDraw: "arco-puxada",
  /** servidor → atirador */
  shotRefused: "flecha-recusada",
  /** servidor → cliente */
  ping: "ping",
  /** cliente → servidor */
  pong: "pong",
  /** servidor → todos */
  arrow: "flecha",
  /** servidor → todos */
  arrowEnd: "flecha-fim",
  /** servidor → todos */
  swing: "golpe",
  /** servidor → todos menos quem colheu */
  harvestSwing: "golpe-coleta",
  /** servidor → todos */
  damage: "dano",
  /** servidor → todos */
  creatureAttack: "ataque-criatura",
  /** servidor → todos */
  hazard: "perigo",
  /** servidor → cliente */
  unlocked: "desbloqueio",
  /** servidor → cliente */
  collected: "coletado",
  /** servidor → quem golpeou */
  harvested: "colheu",
  /** servidor → cliente */
  notice: "aviso",
  /** cliente → servidor (fala) e servidor → quem pode ver (fala repassada) */
  chat: "chat",
  /** cliente → servidor */
  command: "comando",
  /** cliente → servidor */
  mark: "marcar",
  /** servidor → grupo (ou só quem marcou, na Treta) */
  marked: "marca",
  /** servidor → todos */
  system: "sistema",
} as const;

/** Avisos do servidor para o cliente (erro de regra, expulsão, mundo divergente…). */
export const NOTICE_CODES = [
  "reviver",
  "encanto",
  "saci",
  "jangada",
  "simpatia",
  "mascate",
  "so-o-dono",
  "fase-invalida",
  "jogadores-insuficientes",
  "faltam-prontos",
  "sala-publica",
  "expulso",
  "mundo-divergente",
  "carregamento-falhou",
  "ferramenta-insuficiente",
  "inventario",
  "fabricacao",
  "posicionamento",
  "construcao",
  "chat",
  "marcar",
  "votacao",
  "destravar",
] as const;
export type NoticeCode = (typeof NOTICE_CODES)[number];
export interface Notice {
  code: NoticeCode;
  message: string;
}

/** Habilidade usada (F65): quem, qual, onde (o centro do efeito) e por quanto tempo o efeito dura. */
export interface SkillEvent {
  by: string;
  skill: string;
  x: number;
  z: number;
  /** Raio do efeito (m) e duração (ms) para o desenho. */
  radius: number;
  ms: number;
  /** Fim da linha, nos efeitos em linha (F67: Disparo Rompe-Núcleo). */
  x2?: number;
  z2?: number;
}

/** Um Elo fechou (F67): onde e quem participou (a cor de cada um). */
export interface EloEvent {
  x: number;
  z: number;
  by: string[];
  /** Bônus do próximo golpe (fração). */
  bonus: number;
}

/** Resposta a quem usou (F65): `ok` com a recarga em ms, ou o motivo da recusa. */
export interface SkillAck {
  slot: "ativa" | "ultimate";
  ok: boolean;
  cooldownMs: number;
  reason?: string;
}
